import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Mountain } from "lucide-react";
import { SECTIONS, SKILL_PACKS } from "../data/content";

export function scrollToTarget(target) {
  const lenis = window.__lenis;
  if (lenis) {
    lenis.scrollTo(target, { offset: -76, duration: 1.4 });
    if (typeof target === "string") {
      // Sections further down can change height mid-flight (reveals + image loads),
      // leaving long scrolls short of the target — reconcile once things settle.
      setTimeout(() => {
        const el = document.querySelector(target);
        if (!el) return;
        const drift = Math.abs(el.getBoundingClientRect().top - 80);
        if (drift > 120) lenis.scrollTo(el, { offset: -76, duration: 0.8 });
      }, 1600);
    }
    return;
  }
  if (typeof target === "string") {
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

export const Reveal = ({ children, delay = 0, y = 32, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export function SectionHeader({ index, station, title, sub, id }) {
  return (
    <div className="mb-14 md:mb-20">
      <Reveal>
        <div className="flex flex-wrap items-center gap-4 font-mono text-[10px] tracking-[0.3em] text-ember md:text-[11px]">
          <span className="border border-ember/40 px-2 py-1">{index}</span>
          <span className="text-slate-400">STATION {index} — {station}</span>
          <span className="hidden h-px flex-1 bg-white/10 md:inline" />
          <span className="hidden text-slate-500 md:inline">{id}</span>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-6 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {title}
        </h2>
      </Reveal>
      {sub && (
        <Reveal delay={0.14}>
          <p className="mt-4 max-w-2xl text-base text-slate-400 md:text-lg">{sub}</p>
        </Reveal>
      )}
    </div>
  );
}

export function TopoLines({ className = "" }) {
  return (
    <svg viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice" className={className} aria-hidden="true">
      <g fill="none" stroke="#FF6B35" strokeWidth="1">
        <path d="M120 320 C 180 180, 340 140, 440 220 S 660 440, 800 340" strokeOpacity="0.10" />
        <path d="M100 360 C 170 220, 350 180, 460 260 S 680 470, 810 380" strokeOpacity="0.08" />
        <path d="M140 280 C 200 140, 330 100, 430 180 S 640 410, 790 300" strokeOpacity="0.07" />
        <path d="M80 400 C 160 260, 360 220, 480 300 S 700 500, 820 420" strokeOpacity="0.06" />
        <path d="M200 200 C 260 100, 360 60, 440 120 S 600 330, 760 240" strokeOpacity="0.05" />
      </g>
      <g stroke="#38BDF8" strokeOpacity="0.18" strokeWidth="1.2">
        <path d="M700 80 h16 M708 72 v16" />
        <path d="M80 520 h16 M88 512 v16" />
      </g>
    </svg>
  );
}

export function Marquee() {
  const items = SKILL_PACKS.flatMap((p) => p.skills);
  const Row = ({ hidden }) => (
    <div className="flex" aria-hidden={hidden ? "true" : undefined}>
      {items.map((s) => (
        <span key={s} className="flex items-center gap-8 pr-8">
          <span className="whitespace-nowrap font-display text-2xl font-bold uppercase tracking-wide text-slate-200/90 md:text-4xl">
            {s}
          </span>
          <Mountain className="h-5 w-5 shrink-0 text-ember" />
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative overflow-hidden border-y border-white/10 bg-ink/60 py-6" data-testid="skills-marquee">
      <div className="marquee-track flex w-max">
        <Row />
        <Row hidden />
      </div>
    </div>
  );
}

export function RouteHUD() {
  const [active, setActive] = useState("hero");
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && setActive(en.target.id)),
      { rootMargin: "-45% 0px -45% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);
  return (
    <div className="fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-end gap-4 xl:flex" data-testid="route-hud">
      {SECTIONS.map((s) => (
        <button key={s.id} onClick={() => scrollToTarget(`#${s.id}`)} data-testid={`route-hud-${s.id}`} className="group flex items-center gap-3">
          <span className={`font-mono text-[9px] tracking-[0.25em] transition-all duration-300 ${active === s.id ? "text-ember opacity-100" : "text-slate-500 opacity-0 group-hover:opacity-100"}`}>
            {s.label}
          </span>
          <span className={`block h-2.5 w-2.5 rotate-45 border transition-all duration-300 ${active === s.id ? "border-ember bg-ember" : "border-slate-600 bg-night group-hover:border-ember"}`} />
        </button>
      ))}
    </div>
  );
}

export function useISTClock() {
  const [now, setNow] = useState("--:--:--");
  useEffect(() => {
    const tick = () =>
      setNow(
        new Intl.DateTimeFormat("en-GB", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).format(new Date())
      );
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function Counter({ to, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => {
      setV((p) => {
        if (p >= to) {
          clearInterval(t);
          return to;
        }
        return p + 1;
      });
    }, 70);
    return () => clearInterval(t);
  }, [inView, to]);
  return <span ref={ref}>{v}{suffix}</span>;
}
